/**
 * The phone's Back button closes whatever is open on top of the page.
 *
 * Sheets, dialogs, the menu drawer and chat are state, not routes, so Back
 * used to skip past them: the URL changed underneath while the sheet stayed
 * on screen (sheets live in UIProvider, above the router), and Back looked
 * broken. On a phone Back IS the close button — people reach for it first.
 *
 * While `open`, the overlay owns one extra history entry at the SAME url,
 * tagged with its id. Back pops it and the overlay closes. Closed any other
 * way (Cancel, ✕, saving) it takes its entry back off with history.back(), so
 * the next Back press leaves the page as it should.
 *
 * Stacks: each overlay closes only when history moves BELOW its own entry, so
 * a dialog over a sheet closes first, then the sheet.
 *
 * StrictMode runs every effect twice in dev (mount → cleanup → mount). The
 * cleanup therefore waits a tick before popping: if the effect ran again in
 * the meantime the overlay is still open and keeps its entry.
 *
 * Leaving the page from inside an overlay (a menu item in the drawer, a
 * sheet's button that navigates) buries the overlay's entry under the new
 * page, where it cannot be removed. It is remembered as ABANDONED instead,
 * and when Back later lands on it, it is skipped with one more step back —
 * otherwise Back from the new page would show the old page twice and look
 * broken all over again. (Forward onto such an entry is skipped the same way;
 * nobody drives a phone with Forward.)
 */

import { useEffect, useRef } from 'react';

let seq = 0;
const abandoned = new Set();

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    if (abandoned.has(depthOf(window.history.state))) window.history.back();
  });
}

function depthOf(state) {
  return typeof state?.__overlay === 'number' ? state.__overlay : 0;
}

export function useBackToClose(open, onClose) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const idRef = useRef(null);
  const runRef = useRef(0);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;
    const run = ++runRef.current;

    // Re-use our entry across a StrictMode re-run; push a fresh one otherwise.
    let id = idRef.current;
    if (id === null || depthOf(window.history.state) !== id) {
      id = ++seq;
      idRef.current = id;
      window.history.pushState({ ...window.history.state, __overlay: id }, '');
    }

    const onPop = () => {
      // Still at or above our entry: Back closed something stacked on us.
      if (depthOf(window.history.state) >= id) return;
      idRef.current = null;
      closeRef.current?.();
    };
    window.addEventListener('popstate', onPop);

    return () => {
      window.removeEventListener('popstate', onPop);
      setTimeout(() => {
        // Reading the LATEST run is the point: a newer run means re-opened.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (runRef.current !== run) return; // re-opened — keep the entry
        if (idRef.current !== id) return; // Back already took it
        idRef.current = null;
        if (depthOf(window.history.state) === id) window.history.back();
        else abandoned.add(id); // a navigation landed on top of it
      }, 0);
    };
  }, [open]);
}

/**
 * The same, as a component — for dialogs written inline in a page's JSX,
 * where there is no component body to call a hook from. Render it inside the
 * dialog: `<BackToClose onClose={() => setDialog(null)} />`.
 */
export function BackToClose({ onClose }) {
  useBackToClose(true, onClose);
  return null;
}
